import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { FaCheck, FaCircle, FaTimes, FaTrash } from "react-icons/fa";
import { LuCopy, LuCopyCheck } from "react-icons/lu";
import eventsAPI from "../../../api/eventsAPI";
import { useEvent } from "../../../context/EventContext";
import { toast } from "react-toastify";

// * This component renders each message in a card with an option to view the message details, accept, reject, or delete the message.
//  * The component also has a checkbox associated with each message that can be used to select multiple messages and delete them together.
//  * The component also displays the total number of messages in the user's inbox.
const Messages = () => {
  const {
    setLoading,
    loading,
    setAcceptedInvitations,
    setRejectedInvitations,
  } = useEvent();
  const { user, setUser } = useAuth(); // Assume setUser is available to update user state
  const [selectedMessage, setSelectedMessage] = useState(null); // State to track the selected message
  const [showCheckboxes, setShowCheckboxes] = useState(false); // Toggle to show checkboxes
  const [selectedMessages, setSelectedMessages] = useState([]); // Track selected messages

  // Check if user exists and messages are available
  if (!user || !user.messages || user.messages.length === 0) {
    return <p>No messages found.</p>; // If no messages exist, return this fallback
  }

  //   * Handles the "View" button click for a message.
  //   * If the message is already marked as "Read", it will set the selected message
  //   * without making the API call. If the message is not marked as "Read", it will
  //   * update the message's read status in the backend and then update the message
  //   * read status in the UI.
  const handleViewClick = async (message) => {
    // Check if the message has already been marked as "Read"
    if (message.Read === true) {
      // If the message is already read, set the selected message without hitting the API
      setSelectedMessage(message);
      return; // Exit early without making the API call
    }
    try {
      // Step 1: Update the message's read status in the backend
      const response = await eventsAPI.post("/profile/updateStatus", {
        messageID: message._id, // Send the message ID to update
      });

      if (response.status === 200) {
        // Step 2: Update the message read status in the UI
        const updatedMessages = user.messages.map((msg) =>
          msg._id === message._id ? { ...msg, Read: true } : msg
        );

        setUser({ ...user, messages: updatedMessages });

        // Step 3: Set the selected message for viewing
        setSelectedMessage(message);
      }
    } catch (error) {
      console.error("Failed to mark message as read", error);
    }
  };

  // Function to handle the "Back" button click
  const handleBackClick = () => {
    setSelectedMessage(null); // Reset the selected message to go back to the list
  };

  // Toggle showing checkboxes
  const toggleCheckboxes = () => {
    setShowCheckboxes(!showCheckboxes);
    setSelectedMessages([]); // Reset selected messages when toggling
  };

  // Handle individual checkbox click
  const handleCheckboxChange = (id) => {
    if (selectedMessages.includes(id)) {
      setSelectedMessages(
        selectedMessages.filter((messageId) => messageId !== id)
      );
    } else {
      setSelectedMessages([...selectedMessages, id]);
    }
  };

  // Handle select all checkboxes
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allMessageIds = user.messages.map((msg) => msg._id);
      setSelectedMessages(allMessageIds);
    } else {
      setSelectedMessages([]);
    }
  };

  //   * Handles the acceptance of an invitation by sending a POST request to the backend to accept the invitation.
  //   * If successful, updates the list of accepted invitations, removes the accepted message from user messages,
  //   * and displays a success message. If the request fails, logs an error message.
  const handleAccept = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post("/profile/acceptInvitation", {
        meetingID: selectedMessage.MetaData._id,
        userID: user.id,
      });

      if (response.status === 200) {
        toast.success("Invitation Accepted");

        // Update accepted invitations
        setAcceptedInvitations((prev) => [...prev, selectedMessage.MetaData]);

        // Remove the accepted message from user.messages
        // const updatedMessages = user.messages.filter(
        //   (msg) => msg._id !== selectedMessage._id
        // );
        // setUser({ ...user, messages: updatedMessages });

        setLoading(false);
        setSelectedMessage(null);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  //   * Handles the rejection of an invitation by sending a POST request to the backend to reject the invitation.
  //   * If successful, updates the list of rejected invitations, removes the rejected message from user messages,
  //   * and displays a success message. If the request fails, logs an error message.
  const handleReject = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.post("/profile/rejectInvitation", {
        meetingID: selectedMessage.MetaData._id,
        userID: user.id,
      });

      if (response.status === 200) {
        toast.success("Invitation Rejected");

        // Update accepted invitations
        setRejectedInvitations((prev) => [...prev, selectedMessage.MetaData]);

        // Remove the rejected message from user.messages
        // const updatedMessages = user.messages.filter(
        //   (msg) => msg._id !== selectedMessage._id
        // );
        // setUser({ ...user, messages: updatedMessages });

        setLoading(false);
        setSelectedMessage(null);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  //   * Handles the deletion of a message by sending a DELETE request to the backend.
  //   * If successful, it removes the message from the user's messages and shows a success message.
  //   * If the request fails, it shows an error message.
  const handleDelete = async (e, id) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await eventsAPI.delete(
        `/profile/deleteInvitation/${id}`
      );

      if (response.status === 200) {
        toast.success("Location deleted successfully!");

        // Update messages by removing the deleted one
        const updatedMessages = user.messages.filter((msg) => msg._id !== id);
        setUser({ ...user, messages: updatedMessages });
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete the message");
    }
  };

  //   * Handles the deletion of all the selected messages by sending a DELETE request to the backend.
  //   * If successful, it removes the messages from the user's messages and shows a success message.
  //   * If the request fails, it shows an error message.
  //   * It also clears the selected messages after deletion.
  const handleDeleteSelectedAll = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (selectedMessages.length === 0) {
      toast.error("No messages selected for deletion.");
      setLoading(false);
      return;
    }

    try {
      // Make a DELETE request with selected message IDs
      const response = await eventsAPI.delete(`/profile/deleteInvitation`, {
        data: { messageIDs: selectedMessages }, // Send the selected message IDs to the backend
      });

      if (response.status === 200) {
        toast.success("Messages deleted successfully!");

        // Update messages by removing the deleted ones from the frontend
        const updatedMessages = user.messages.filter(
          (msg) => !selectedMessages.includes(msg._id)
        );
        setUser({ ...user, messages: updatedMessages });

        // Clear selected messages after deletion
        setSelectedMessages([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete the selected messages");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="message-section">
      {/* Toggle button to show/hide checkboxes */}
      <button className="add-space" onClick={toggleCheckboxes}>
        {showCheckboxes ? <LuCopyCheck /> : <LuCopy />}
      </button>

      {/* Show Select All checkbox and Delete button if checkboxes are visible */}
      {showCheckboxes && (
        <div className="selection-controls">
          <div className="checkbox-top">
            <input
              type="checkbox"
              onChange={handleSelectAll}
              className="checkbox"
              checked={
                selectedMessages.length === user.messages.length &&
                user.messages.length > 0
              }
            />
            <span>Select All</span>
          </div>

          {/* Delete button enabled only when messages are selected */}
          <button
            className="add-space"
            onClick={handleDeleteSelectedAll}
            disabled={selectedMessages.length === 0 || loading}
          >
            <FaTrash />
          </button>
        </div>
      )}

      {/* If a message is selected, show its details */}
      {selectedMessage ? (
        <div className="message-details">
          <h2>Meeting Details</h2>
          {selectedMessage.MetaData ? (
            <div>
              <p>
                Event Name: {selectedMessage.MetaData.mainEventID.event_name}
              </p>
              <p>
                Event Type: {selectedMessage.MetaData.mainEventID.event_type}
              </p>
              <p>
                Organized By:{" "}
                {selectedMessage.MetaData.mainEventID.organized_by}
              </p>
              <p>{`Registration Starts from: ${new Date(
                selectedMessage.MetaData.mainEventID.registration_start_date
              ).toLocaleDateString()}`}</p>
              <p>Title: {selectedMessage.MetaData.agenda}</p>
              <p>
                Date:{" "}
                {new Date(selectedMessage.MetaData.start).toLocaleString()}
              </p>
            </div>
          ) : (
            <p>No meeting details available.</p>
          )}

          {/* Accept and Reject buttons */}
          {selectedMessage.Type === "Invitation Accepted" ? (
            <span
              style={{ color: "green", fontWeight: "bold", fontSize: "20px" }}
            >
              Invitation Accepted
            </span>
          ) : selectedMessage.Type === "Rejected" ? (
            <span
              style={{ color: "red", fontWeight: "bold", fontSize: "20px" }}
            >
              Invitation Rejected
            </span>
          ) : (
            <div className="request-buttons">
              <button className="accept" onClick={handleAccept}>
                {loading ? <FaCheck /> : "Accept"}
              </button>
              <button className="reject" onClick={handleReject}>
                {loading ? <FaTimes /> : "Reject"}
              </button>
            </div>
          )}

          {/* Back button */}
          <button className="add-space" onClick={handleBackClick}>
            Back to Messages
          </button>
        </div>
      ) : (
        <div>
          {/* Map through the messages array and render each message */}
          {user.messages.length === 0 ? (
            <p>No messages found.</p>
          ) : (
            user.messages.map((message, index) => (
              <div key={index} className="message-card">
                {/* Checkbox for each message */}
                {showCheckboxes && (
                  <input
                    type="checkbox"
                    className="checkbox"
                    onChange={() => handleCheckboxChange(message._id)}
                    checked={selectedMessages.includes(message._id)}
                  />
                )}

                <p>
                  {message.From
                    ? `${message.From.firstName} ${message.From.lastName} sent you a Meeting Invitation`
                    : "Unknown Sender"}
                </p>
                <div className="status">
                  {/* <p>{message.Read === true ? "Read" : "Unread"}</p> */}
                  {message.Read === true ? (
                    <>
                      <FaCircle
                        style={{
                          color: "rgb(60, 249, 60)",
                          height: "10px",
                          width: "10px",
                        }}
                      />
                      <p>Read</p>
                    </>
                  ) : (
                    <>
                      <FaCircle
                        style={{
                          color: "rgb(60, 249, 60)",
                          height: "10px",
                          width: "10px",
                        }}
                      />
                      <p>Unread</p>
                    </>
                  )}
                </div>

                {/* View and Delete buttons */}
                <div className="request-button">
                  <button
                    className="add-space"
                    onClick={() => handleViewClick(message)}
                  >
                    View
                  </button>
                  <button
                    className="add-space"
                    onClick={(e) => handleDelete(e, message._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Messages;
